import qs from "qs";
import hbscan from "../../api/hbscan";
import IFloor from "../../models/floor";
import { IDashboardData } from "../../models/globalDashboard/dashboardScan";
import ILocal from "../../models/local";
import { IProcessedScan, IScan } from "../../models/stat";
import Auth from "../auth/AuthService";

class HBScanStatService {
    async getMapScansByBuilding(
        localIds: number[],
        start: Date,
        end: Date,
    ): Promise<IProcessedScan[]> {
        const response = await hbscan.post<IProcessedScan[]>(
            "/api/hbscan/dashboard/GetMapScansByBuilding",
            {
                localIds,
                start,
                end,
            },
            {
                headers: {
                    Authorization: Auth?.user?.guid || "",
                    "Content-Type": "application/json",
                },
            },
        );

        return response.data;
    }

    async getScans(
        localIds: number[],
        start: Date,
        end: Date,
        propertyGroupId: number,
    ): Promise<IProcessedScan[]> {
        return (
            await hbscan.get<IProcessedScan[]>(
                "/api/hbscan/dashboard/getMapScans",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                    params: { start, end, localIds, propertyGroupId },
                    paramsSerializer: (params) => {
                        return qs.stringify(params);
                    },
                },
            )
        ).data;
    }

    async getBuildingScans(
        buildingId: number,
        start: Date,
        end: Date,
    ): Promise<IProcessedScan[]> {
        return (
            await hbscan.get<IProcessedScan[]>(
                "/api/hbscan/dashboard/GetBuildingScans",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                    params: { start, end, buildingId },
                },
            )
        ).data;
    }

    async getCompanyScans(start: Date, end: Date): Promise<IProcessedScan[]> {
        return (
            await hbscan.get<IProcessedScan[]>(
                "/api/hbscan/dashboard/GetCompanyScans",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                    params: { start, end },
                },
            )
        ).data;
    }
    async getInOutScansByCompany(
        start: Date,
        end: Date,
    ): Promise<IProcessedScan[]> {
        return (
            await hbscan.get<IProcessedScan[]>(
                "/api/hbscan/dashboard/GetInOutScansByCompany",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                    params: { start, end },
                },
            )
        ).data;
    }

    async getPropertyGroupScans(
        propertyGroupId: number,
        start: Date,
        end: Date,
    ): Promise<IProcessedScan[]> {
        return (
            await hbscan.get<IProcessedScan[]>(
                "/api/hbscan/dashboard/GetPropertyGroupScans",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                    params: { start, end, propertyGroupId },
                },
            )
        ).data;
    }

    async getDashboardData(
        propertyGroupId: number,
        start: Date,
        end: Date,
        previousStart: Date,
        previousEnd: Date,
    ): Promise<{ data: IDashboardData }> {
        const response = await hbscan.get<IDashboardData>(
            "/api/hbscan/dashboard/GetDashboardData",
            {
                headers: { Authorization: Auth?.user?.guid || "" },
                params: {
                    propertyGroupId,
                    start,
                    end,
                    previousStart,
                    previousEnd,
                },
            },
        );
        return response;
    }

    async getInOutScansByPropertyId(
        propertyGroupId: number,
        start: Date,
        end: Date,
    ): Promise<IProcessedScan[]> {
        return (
            await hbscan.get<IProcessedScan[]>(
                "/api/hbscan/dashboard/GetInOutScansByPropertyGroup",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                    params: { start, end, propertyGroupId },
                },
            )
        ).data;
    }
    async getUserScans(
        userId: number,
        start: Date,
        end: Date,
    ): Promise<IProcessedScan[]> {
        return (
            await hbscan.get<IProcessedScan[]>(
                "/api/hbscan/dashboard/GetUserScans",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                    params: { start, end, userId },
                },
            )
        ).data;
    }
    async getInOutScansByUser(
        userId: number,
        start: Date,
        end: Date,
    ): Promise<IProcessedScan[]> {
        return (
            await hbscan.get<IProcessedScan[]>(
                "/api/hbscan/dashboard/GetInOutScansByUser",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                    params: { start, end, userId },
                },
            )
        ).data;
    }

    async getInOutScansByBuildingId(
        buildingId: number,
        start: Date,
        end: Date,
    ): Promise<IProcessedScan[]> {
        return (
            await hbscan.get<IProcessedScan[]>(
                "/api/hbscan/dashboard/GetInOutScansByBuildingId",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                    params: { start, end, buildingId },
                },
            )
        ).data;
    }
    async getRawScans(
        propertyGroupId: number,
        start: Date,
        end: Date,
    ): Promise<IScan[]> {
        return (
            await hbscan.get<IScan[]>("/api/hbscan/scan/GetRawScans", {
                headers: { Authorization: Auth?.user?.guid || "" },
                params: { start, end, propertyGroupId },
            })
        ).data;
    }

    async getRawScansByUser(userId: number): Promise<IScan[]> {
        return (
            await hbscan.get<IScan[]>("/api/hbscan/scan/GetRawScansByUser", {
                headers: { Authorization: Auth?.user?.guid || "" },
                params: { userId },
            })
        ).data;
    }

    async getLocals(localIds: number[]): Promise<ILocal[]> {
        const url = `/api/hbscan/dashboard/GetLocals`;

        const response = await hbscan.post<ILocal[]>(
            url,
            {
                localIds,
            },
            {
                headers: {
                    Authorization: Auth?.user?.guid || "",
                    "Content-Type": "application/json",
                },
            },
        );

        return response.data;
    }

    async getFloors(): Promise<IFloor[]> {
        return (
            await hbscan.get<IFloor[]>("/api/hbscan/dashboard/getFloors", {
                headers: { Authorization: Auth?.user?.guid || "" },
            })
        ).data;
    }

    async getFloorsByPropertyGroups(
        propertyGroupIds: number[],
    ): Promise<IFloor[]> {
        return (
            await hbscan.get<IFloor[]>(
                "/api/hbscan/dashboard/GetFloorsByPropertyGroups",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                    params: { propertyGroupIds },
                    paramsSerializer: (params) => {
                        return qs.stringify(params, { arrayFormat: "repeat" });
                    },
                },
            )
        ).data;
    }

    async getRooms(): Promise<ILocal[]> {
        return (
            await hbscan.get<ILocal[]>(
                "/api/hbscan/dashboard/GetDashboardEditRooms",
                {
                    headers: { Authorization: Auth?.user?.guid || "" },
                },
            )
        ).data;
    }

    async saveMap(blob: Blob, floorId: number) {
        var formData = new FormData();
        formData.append("floorId", JSON.stringify(floorId));
        formData.append("hbScanMap", blob, "hbScanMap.svg");
        return (
            await hbscan.put("/api/hbscan/dashboard/PutMap", formData, {
                headers: {
                    Authorization: Auth.guid,
                    "Content-Type": "multipart/form-data",
                },
            })
        ).data;
    }
}

const hbscanStatService = new HBScanStatService();

export default hbscanStatService;
